import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLocationContext } from '../../hooks/useLocationContext';
import { ScopedLink } from '../../components/ScopedLink';
import {
  useGetAttendanceConcernsQuery,
  useGetAttendanceRegisterQuery,
  useGetClassesQuery,
} from '../../store/api/localApi';
import type { RegisterDay, RegisterEntry, RegisterSession } from '../../types/attendance-v2';
import {
  addDays,
  dublinDateString,
  formatCivilDate,
  formatCivilDateWithWeekday,
  formatTime,
} from '../../utils/date';
import './attendance-print.css';

/**
 * The Tusla attendance register — one printable week grid of one class's
 * children x days, plus the attendance-concerns list.
 *
 * One class at a time, on purpose: an inspector asks for "the 3PM register for
 * last week", and a whole-location grid (185 rows at a real site) is neither
 * that nor readable. The class's week arrives in ONE response; batching below
 * only limits how many rows are drawn on screen, and print draws them all.
 *
 * Everything here counts SESSIONS, not days: a child in two classes on one
 * date is expected twice, and the register renders one marker per session.
 * Copy that says "days" would understate what an inspector is looking at.
 *
 * Admin-only. The page gates on isAdmin itself rather than relying on the nav
 * entry being hidden, so a direct URL hit by staff or a parent bounces here
 * (the server 403s the endpoint too — this is the UI half of the same rule).
 */

type Tab = 'register' | 'concerns';

const DAYS_IN_WEEK = 7;

/** Rows drawn per Show-more press. Small enough to scan on a tablet, large
 *  enough that a typical class (30–70) takes one or two presses. */
const BATCH_SIZE = 25;

const CONCERN_WINDOWS = [7, 14, 21, 28] as const;
const DEFAULT_CONCERN_WINDOW = 14;

const ECCE_GUIDANCE_URL =
  'https://www.citizensinformation.ie/en/education/pre-school-education-and-childcare/early-childhood-care-and-education-scheme/';

/** Monday of the civil week containing `date` (YYYY-MM-DD). */
function weekStartOf(date: string): string {
  // Noon-UTC anchor for the same reason addDays uses one: getUTCDay on a
  // date-only string parsed as midnight can land on the previous day in a
  // west-of-UTC viewer timezone.
  const [year, month, day] = date.split('-').map(Number);
  const anchored = new Date(Date.UTC(year, month - 1, day, 12));
  const dayOfWeek = anchored.getUTCDay(); // 0 = Sunday
  const daysSinceMonday = (dayOfWeek + 6) % 7;
  return addDays(date, -daysSinceMonday);
}

/** "2 – 8 Mar 2026" — both ends spelled out; inspectors read the range first. */
function weekLabel(from: string, to: string): string {
  return `${formatCivilDate(from)} – ${formatCivilDate(to)}`;
}

interface ClassOption {
  id: string;
  name: string;
  startTime: string;
  isActive?: boolean;
}

/** Day order — Breakfast Club before Pre School before the afternoon classes —
 *  then name, so the first option is the class that runs first. */
function sortByDayOrder(classes: ClassOption[]): ClassOption[] {
  return [...classes].sort(
    (a, b) => a.startTime.localeCompare(b.startTime) || a.name.localeCompare(b.name)
  );
}

interface CellMarker {
  entry: RegisterEntry;
  className: string;
}

/**
 * Every marker a child has on one date, across all that date's sessions. A
 * child not expected in a session simply has no entry in it, so an empty
 * array means "not expected that day" — rendered as a blank grey cell rather
 * than an absence, which it is not.
 */
function markersFor(day: RegisterDay, studentId: string): CellMarker[] {
  const markers: CellMarker[] = [];
  for (const session of day.sessions) {
    if (session.suppressed) continue;
    for (const entry of session.entries) {
      if (entry.studentId === studentId) {
        markers.push({ entry, className: session.className });
      }
    }
  }
  return markers;
}

/** The closure/cancellation notes on a date, deduplicated for the header. */
function suppressionReasons(day: RegisterDay): string[] {
  const reasons = new Set<string>();
  for (const session of day.sessions) {
    if (session.suppressed) reasons.add(session.suppressed.reason);
  }
  return [...reasons];
}

const FlagIcon: React.FC = () => (
  <svg
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
    className="h-3 w-3 shrink-0 text-amber-600"
  >
    <path d="M3 2.75A.75.75 0 0 1 3.75 2h11.5a.75.75 0 0 1 .6 1.2L13.19 7l2.66 3.8a.75.75 0 0 1-.6 1.2H4.5v5.25a.75.75 0 0 1-1.5 0V2.75Z" />
  </svg>
);

function errorStatus(error: unknown): number | string | undefined {
  return (error as { status?: number | string } | undefined)?.status;
}

const OTHER_COLLECTOR_TITLE = 'Released to someone not on the pickup list';
const NOT_SIGNED_OUT_TITLE = 'Session ended with no sign-out recorded';

/**
 * The pickup note after a PRESENT tick. `pickup` names the cause; an older
 * server that only sends `flaggedPickup` still gets the flag, with a
 * cause-neutral tooltip because the cause is unknown to it.
 */
const PickupNote: React.FC<{ entry: RegisterEntry }> = ({ entry }) => {
  if (entry.pickup === 'NOT_SIGNED_OUT') {
    return (
      <span
        data-testid="attendance-pickup-not-signed-out"
        title={NOT_SIGNED_OUT_TITLE}
        className="text-[11px] text-gray-400"
      >
        · no sign-out
      </span>
    );
  }
  if (entry.pickup === 'OTHER_COLLECTOR') {
    return (
      <span data-testid="attendance-pickup-other-collector" title={OTHER_COLLECTOR_TITLE}>
        <FlagIcon />
      </span>
    );
  }
  if (entry.flaggedPickup) {
    return (
      <span data-testid="attendance-pickup-flagged" title="Pickup flagged for review">
        <FlagIcon />
      </span>
    );
  }
  return null;
};

const ROLLED_IN_TITLE = 'Rolled in from the previous class';
const UNBOOKED_TITLE = 'Was signed in but not booked on this day';

/** True when the child was in without a booking on any day of the shown week. */
function wasInUnbooked(days: Iterable<RegisterDay>, studentId: string): boolean {
  for (const day of days) {
    for (const session of day.sessions) {
      if (session.entries.some((e) => e.studentId === studentId && e.unexpected)) return true;
    }
  }
  return false;
}

/**
 * Who signed the child in: a staff code, the roll-in arrow (the earlier
 * class's tap is never credited here), or nothing for a parent tap or an
 * older server that sends neither.
 */
const SignedInBy: React.FC<{ entry: RegisterEntry; cellKey: string }> = ({ entry, cellKey }) => {
  if (entry.checkInKind === 'ROLLED_IN') {
    return (
      <span data-testid={`attendance-cell-rolled-in-${cellKey}`} title={ROLLED_IN_TITLE}>
        ↪
      </span>
    );
  }
  if (entry.checkInBy) {
    return (
      <span data-testid={`attendance-cell-in-by-${cellKey}`} className="font-semibold">
        {entry.checkInBy}
      </span>
    );
  }
  return null;
};

const Marker: React.FC<{
  marker: CellMarker;
  multiSession: boolean;
  studentId: string;
  date: string;
}> = ({ marker, multiSession, studentId, date }) => {
  const { entry, className } = marker;
  // The class name only earns tooltip space when a day has more than one
  // session — otherwise the column header already says which class it is.
  const title = multiSession ? className : undefined;
  const cellKey = `${studentId}-${date}`;

  if (entry.state === 'PRESENT') {
    return (
      <span
        title={title}
        className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-green-700"
      >
        <span aria-hidden="true">✓</span>
        <span>{formatTime(entry.checkInAt)}</span>
        <SignedInBy entry={entry} cellKey={cellKey} />
        {/* Time and code are independent: a parent sign-out has a time and
            no staff code. */}
        {entry.checkOutAt && (
          <span data-testid={`attendance-cell-out-${cellKey}`}>
            → {formatTime(entry.checkOutAt)}
          </span>
        )}
        {entry.checkOutBy && (
          <span data-testid={`attendance-cell-out-by-${cellKey}`} className="font-semibold">
            {entry.checkOutBy}
          </span>
        )}
        <PickupNote entry={entry} />
        {/* Which day it was: the tag by the name covers the whole week. */}
        {entry.unexpected && (
          <span
            data-testid={`attendance-cell-unbooked-${cellKey}`}
            title={UNBOOKED_TITLE}
            className="text-[11px] text-gray-400"
          >
            · not booked
          </span>
        )}
      </span>
    );
  }

  if (entry.state === 'PLANNED_ABSENT') {
    return (
      <span title={title} className="whitespace-nowrap text-xs text-amber-700">
        Planned
      </span>
    );
  }

  return (
    <span
      title={title}
      className="inline-flex h-5 w-5 items-center justify-center rounded border border-red-400 text-xs text-red-600"
    >
      —
    </span>
  );
};

type FooterRow = 'room' | 'assigned' | 'on-day' | 'register';

const FOOTER_ROWS: Array<{ row: FooterRow; label: string }> = [
  { row: 'room', label: 'Room' },
  { row: 'assigned', label: 'Staff assigned' },
  { row: 'on-day', label: 'Staff on the day' },
  { row: 'register', label: 'Register' },
];

/** One footer cell's content for a session that ran (or was due to run). */
function footerContent(row: FooterRow, session: RegisterSession): React.ReactNode {
  switch (row) {
    case 'room':
      return session.room ?? null;
    case 'assigned':
      // History rule: a session from before the assigned-staff snapshot
      // cannot say who was assigned, so it shows a dash rather than today's
      // list. An empty snapshot means nobody was assigned — an empty cell.
      return session.assignedStaff === undefined ? '—' : session.assignedStaff.join(', ');
    case 'on-day':
      return (session.staffOnDay ?? []).map((staff) => (
        <div key={staff.code} className="text-[11px]">
          <b>{staff.code}</b> {staff.name}
        </div>
      ));
    case 'register': {
      // No materialised session row: nothing was ever due to be opened.
      if (!session.sessionId) return null;
      const { openedBy, closedBy } = session;
      if (!openedBy && !closedBy) return 'Not started';
      return (
        <>
          {openedBy && (
            <div>
              Opened {formatTime(openedBy.at)} {openedBy.name}
            </div>
          )}
          {closedBy && (
            <div>
              Closed {formatTime(closedBy.at)} {closedBy.name}
            </div>
          )}
        </>
      );
    }
  }
}

/**
 * The four per-date rows under the children: the room, who was assigned to
 * the class, who actually signed children in or out (the key to the codes in
 * the cells above), and who opened and closed the register. A day the class
 * did not run, or was closed, is a blank grey cell like a child's.
 */
const RegisterFooter: React.FC<{
  weekDates: string[];
  sessionOn: (date: string) => RegisterSession | undefined;
}> = ({ weekDates, sessionOn }) => (
  <tbody
    data-testid="attendance-footer"
    className="divide-y divide-gray-200 border-t-2 border-gray-300"
  >
    {FOOTER_ROWS.map(({ row, label }) => (
      <tr key={row} data-testid={`attendance-row-${row}`}>
        <td className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700">
          {label}
        </td>
        {weekDates.map((date) => {
          const session = sessionOn(date);
          const ran = session && !session.suppressed;
          return (
            <td
              key={date}
              data-testid={`attendance-footer-${row}-${date}`}
              className={`px-3 py-2 align-top text-xs text-gray-700 ${ran ? '' : 'bg-gray-50'}`}
            >
              {ran && footerContent(row, session)}
            </td>
          );
        })}
      </tr>
    ))}
  </tbody>
);

const AttendancePage: React.FC = () => {
  const params = useParams<{ locationId?: string; locId?: string }>();
  const { activeLocationId, activeLocationName } = useLocationContext();
  const locationId = params.locationId || params.locId || activeLocationId || undefined;
  const { isAdmin } = useAuth();

  const [tab, setTab] = useState<Tab>('register');
  const [weekStart, setWeekStart] = useState<string>(() => weekStartOf(dublinDateString()));
  const [windowDays, setWindowDays] = useState<number>(DEFAULT_CONCERN_WINDOW);
  const [classId, setClassId] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const weekEnd = addDays(weekStart, DAYS_IN_WEEK - 1);

  const {
    data: classesData,
    isLoading: classesLoading,
    isError: classesError,
    refetch: refetchClasses,
  } = useGetClassesQuery(locationId ?? '', {
    skip: !locationId || !isAdmin,
  });
  // Deactivated classes stay out of the picker: the endpoint returns them, and
  // sorted first by start time one could otherwise become the landing view
  // for every admin, showing an empty register. `!== false` because closing a
  // class no longer writes `isActive` at all — an absent flag must stay listed.
  const classes = useMemo(
    () => sortByDayOrder((classesData ?? []).filter((c) => c.isActive !== false)),
    [classesData]
  );

  // The first class in day order, every time the list loads. Nothing is
  // remembered between visits: a stored id can name a class that has since
  // been closed or deleted, and re-picking costs one click.
  useEffect(() => {
    if (classes.length === 0) return;
    if (!classId || !classes.some((c) => c.id === classId)) setClassId(classes[0].id);
  }, [classes, classId]);

  // Any change of class, week or search starts again from the first batch.
  useEffect(() => {
    setVisibleCount(BATCH_SIZE);
  }, [classId, weekStart, search]);

  const selectedClass = classes.find((c) => c.id === classId);

  // `currentData`, not `data`: RTK keeps the PREVIOUS argument's result in
  // `data` while a new one is in flight, so a class or week change would show
  // the old class's children under the new class's name (and print header)
  // until the response landed. `currentData` is undefined for that moment and
  // the panel shows a spinner instead.
  const {
    currentData: data,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetAttendanceRegisterQuery(
    { locationId: locationId ?? '', from: weekStart, to: weekEnd, classId },
    {
      skip: !locationId || !isAdmin || tab !== 'register' || !classId,
      // Sign-ins land from tablets, not through this page's cache: every
      // visit reads the sheet fresh rather than RTK's cached copy.
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    currentData: concernsData,
    isFetching: concernsFetching,
    isError: concernsError,
    refetch: refetchConcerns,
  } = useGetAttendanceConcernsQuery(
    { locationId: locationId ?? '', windowDays },
    {
      skip: !locationId || !isAdmin || tab !== 'concerns',
      refetchOnMountOrArgChange: true,
    }
  );

  // The seven dates of the shown week, whether or not the server returned a
  // day row for each — a week with no Friday classes still needs a Friday
  // column, or the grid silently changes shape week to week.
  const weekDates = useMemo(
    () => Array.from({ length: DAYS_IN_WEEK }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const dayByDate = useMemo(() => {
    const map = new Map<string, RegisterDay>();
    for (const day of data?.days ?? []) map.set(day.date, day);
    return map;
  }, [data]);

  const students = useMemo(
    () => [...(data?.students ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data]
  );

  // Rows are ALL rendered — the ones outside the search or beyond the current
  // batch are hidden on screen and shown on paper (`print:table-row`), because
  // the printed register is the whole class whatever the screen was showing.
  const needle = search.trim().toLowerCase();
  const matching = useMemo(
    () => (needle ? students.filter((s) => s.name.toLowerCase().includes(needle)) : students),
    [students, needle]
  );
  const shownIds = useMemo(
    () => new Set(matching.slice(0, visibleCount).map((s) => s.id)),
    [matching, visibleCount]
  );
  const remaining = Math.max(0, matching.length - shownIds.size);

  if (!isAdmin) {
    // Distinct testid rather than merely the absence of the register, so a
    // test can assert this branch positively (see StaffComplianceRegister).
    return (
      <div
        data-testid="attendance-page-denied"
        className="min-h-screen flex items-center justify-center text-sm text-slate-500"
      >
        You don&apos;t have access to this page.
      </div>
    );
  }

  if (!locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  const hasAnySession = (data?.days ?? []).some((d) => d.sessions.length > 0);

  const registerPanel = (() => {
    // Order matters: a classes failure must never fall through to the empty
    // state, and the first paint (classes still loading, register skipped)
    // must be a spinner, not a false "no sessions this week".
    if (classesLoading || (!classesData && !classesError)) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      );
    }

    if (classesError) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-slate-500">
          <p className="text-base font-medium text-slate-700">
            Failed to load the classes for this location.
          </p>
          <button
            type="button"
            data-testid="attendance-classes-retry"
            onClick={() => refetchClasses()}
            className="text-primary-600 hover:text-primary-500 underline"
          >
            Try again
          </button>
        </div>
      );
    }

    if (classes.length === 0) {
      return (
        <div
          data-testid="attendance-register-no-classes"
          className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500"
        >
          No classes at this location yet.
        </div>
      );
    }

    if (isError) {
      // A class that vanished between the picker loading and the request is a
      // 404 the user cannot retry out of; reload the classes so the default
      // re-picks instead of re-sending the dead id.
      const classGone = errorStatus(error) === 404;
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-slate-500">
          <p className="text-base font-medium text-slate-700">
            {classGone
              ? 'That class no longer exists at this location.'
              : 'Failed to load the attendance register.'}
          </p>
          <button
            type="button"
            data-testid="attendance-register-retry"
            onClick={() => (classGone ? refetchClasses() : refetch())}
            className="text-primary-600 hover:text-primary-500 underline"
          >
            {classGone ? 'Reload classes' : 'Try again'}
          </button>
        </div>
      );
    }

    if (isFetching || !data) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      );
    }

    if (!hasAnySession) {
      return (
        <div
          data-testid="attendance-register-empty"
          className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500"
        >
          {selectedClass?.name ?? 'This class'} has no sessions this week.
        </div>
      );
    }

    if (students.length === 0) {
      return (
        <div
          data-testid="attendance-register-nobody"
          className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500"
        >
          {selectedClass?.name ?? 'This class'} ran this week with no children enrolled.
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="attendance-register-scroll overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table data-testid="attendance-register-grid" className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Child
                </th>
                {weekDates.map((date) => {
                  const reasons = suppressionReasons(dayByDate.get(date) ?? { date, sessions: [] });
                  return (
                    <th
                      key={date}
                      data-testid={`attendance-col-${date}`}
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500"
                    >
                      <div className="uppercase tracking-wider">{formatCivilDate(date)}</div>
                      {reasons.length > 0 && (
                        <div className="mt-0.5 whitespace-normal text-[11px] font-normal normal-case text-gray-400">
                          {reasons.join(', ')}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {students.map((student) => {
                const shown = shownIds.has(student.id);
                return (
                  <tr
                    key={student.id}
                    data-testid={`attendance-row-${student.id}`}
                    data-shown={shown ? 'true' : 'false'}
                    className={shown ? undefined : 'hidden print:table-row'}
                  >
                    <td className="sticky left-0 z-10 bg-white px-3 py-2 text-sm font-medium text-gray-900">
                      {student.name}
                      {wasInUnbooked(dayByDate.values(), student.id) && (
                        <span
                          data-testid={`register-unexpected-${student.id}`}
                          title={UNBOOKED_TITLE}
                          className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-normal text-gray-500"
                        >
                          not booked
                        </span>
                      )}
                    </td>
                    {weekDates.map((date) => {
                      const day = dayByDate.get(date) ?? { date, sessions: [] };
                      const markers = markersFor(day, student.id);
                      return (
                        <td
                          key={date}
                          data-testid={`attendance-cell-${student.id}-${date}`}
                          className={`px-3 py-2 align-top ${
                            markers.length === 0 ? 'bg-gray-50' : ''
                          }`}
                        >
                          <div className="flex flex-col gap-1">
                            {markers.map((marker, i) => (
                              <Marker
                                key={`${marker.className}-${i}`}
                                marker={marker}
                                multiSession={markers.length > 1}
                                studentId={student.id}
                                date={date}
                              />
                            ))}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
            <RegisterFooter
              weekDates={weekDates}
              sessionOn={(date) =>
                dayByDate.get(date)?.sessions.find((session) => session.classId === classId)
              }
            />
          </table>
          {remaining > 0 && (
            <div className="attendance-print-hide flex items-center gap-3 border-t border-gray-200 bg-gray-50 px-3 py-2">
              <button
                type="button"
                data-testid="attendance-show-more"
                onClick={() => setVisibleCount((n) => n + BATCH_SIZE)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Show {Math.min(BATCH_SIZE, remaining)} more
              </button>
              <span className="text-xs text-gray-500">
                {shownIds.size} of {matching.length} shown · sorted by name
              </span>
            </div>
          )}
          {needle && matching.length === 0 && (
            <div
              data-testid="attendance-search-empty"
              className="attendance-print-hide px-3 py-3 text-sm text-gray-500"
            >
              No child in this class matches “{search.trim()}”.
            </div>
          )}
        </div>

        <p data-testid="attendance-code-key" className="text-[11px] text-gray-500">
          ✓ time in and the staff member who signed the child in · → time out and the staff member
          who signed the child out · codes are named under &quot;Staff on the day&quot; for each
          date.
        </p>

        <div
          data-testid="attendance-legend"
          className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500"
        >
          <span>
            <span className="text-green-700">✓ 15:04</span> arrived
          </span>
          <span>
            <span className="text-amber-700">Planned</span> absence filed
          </span>
          <span>
            <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-red-400 text-[10px] text-red-600">
              —
            </span>{' '}
            unexplained
          </span>
          <span className="inline-flex items-center gap-1">
            <FlagIcon /> released to someone not on the pickup list
          </span>
          <span>
            <span className="text-gray-400">· no sign-out</span> session ended without a sign-out
          </span>
          <span>
            <span className="text-gray-400">· not booked</span> signed in on a day the child was not
            booked
          </span>
          <span className="text-gray-400">blank = not enrolled that day</span>
        </div>
      </div>
    );
  })();

  const concernsPanel = (() => {
    if (concernsFetching || (!concernsData && !concernsError)) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      );
    }

    if (concernsError) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-slate-500">
          <p className="text-base font-medium text-slate-700">
            Failed to load attendance concerns.
          </p>
          <button
            type="button"
            data-testid="attendance-concerns-retry"
            onClick={() => refetchConcerns()}
            className="text-primary-600 hover:text-primary-500 underline"
          >
            Try again
          </button>
        </div>
      );
    }

    const concerns = concernsData?.concerns ?? [];
    const window = concernsData?.window;
    const classesStartedInsideWindow =
      window?.firstClassDay !== undefined && window.firstClassDay > window.from;

    const windowLine = window && (
      <p data-testid="attendance-concerns-window" className="text-sm text-gray-600">
        Last {window.days} days ·{' '}
        <span className="font-medium text-gray-900">{weekLabel(window.from, window.to)}</span>
        {classesStartedInsideWindow && (
          <>
            {' '}
            · Classes have run since{' '}
            <span className="font-medium text-gray-900">
              {formatCivilDateWithWeekday(window.firstClassDay)}
            </span>
            ; earlier days are not counted.
          </>
        )}
      </p>
    );

    if (concerns.length === 0) {
      return (
        <div className="space-y-3">
          {windowLine}
          <div
            data-testid="attendance-concerns-empty"
            className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500"
          >
            Every child with expected sessions attended at least once in the last {windowDays} days.
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {windowLine}
        <ul
          data-testid="attendance-concerns-list"
          className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white shadow-sm"
        >
          {concerns.map((concern) => {
            const n = concern.expectedSessions;
            return (
              <li
                key={concern.studentId}
                data-testid={`attendance-concern-${concern.studentId}`}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
              >
                <div className="min-w-0">
                  <ScopedLink
                    to={`/students/${concern.studentId}`}
                    data-testid={`attendance-concern-link-${concern.studentId}`}
                    className="text-sm font-medium text-primary-700 hover:text-primary-600 hover:underline"
                  >
                    {concern.name}
                  </ScopedLink>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {/* Sessions, never days — a child in two classes on one date is
                        expected twice, and the count the inspector reads is per
                        session. "since <date>" says when the expectation started;
                        the window button alone made a 10-day term read as 28. */}
                    Expected{' '}
                    <span
                      data-testid={`attendance-concern-since-${concern.studentId}`}
                      className="text-gray-700"
                    >
                      {n} {n === 1 ? 'session' : 'sessions'} since{' '}
                      {formatCivilDateWithWeekday(concern.firstExpectedDate)}
                    </span>{' '}
                    ·{' '}
                    {concern.allExplained
                      ? `all ${n} have an absence filed`
                      : concern.lastAttendedDate
                      ? `last attended ${formatCivilDate(concern.lastAttendedDate)}`
                      : 'never attended'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <ScopedLink
                    to={`/students/${concern.studentId}`}
                    data-testid={`attendance-concern-history-${concern.studentId}`}
                    className="text-xs text-primary-700 hover:text-primary-600 hover:underline"
                  >
                    History
                  </ScopedLink>
                  {concern.allExplained ? (
                    <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                      All absences explained
                    </span>
                  ) : (
                    <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                      Unexplained
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        <p data-testid="attendance-concerns-footnote" className="text-xs text-gray-400">
          Children who have left the service are not listed.
        </p>
      </div>
    );
  })();

  return (
    <div data-testid="attendance-page" className="space-y-6 px-4 pt-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>
          <p className="mt-1 text-sm text-gray-600">
            The register of expected sessions and who attended them. Print it for a Tusla
            inspection.
          </p>
        </div>
        <button
          type="button"
          data-testid="attendance-print-btn"
          onClick={() => window.print()}
          className="attendance-print-hide mt-4 inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:mt-0"
        >
          Print
        </button>
      </div>

      {/* Print-only: a loose page must still say which location, which class
          and which week (or which window) it covers. Hidden on screen by
          `hidden`; surfaced by the print CSS. */}
      <div data-testid="attendance-print-header" className="attendance-print-only hidden">
        {tab === 'register' ? (
          <>
            <div className="text-base font-semibold">
              Attendance register — {activeLocationName ?? 'this location'}
              {selectedClass ? ` · ${selectedClass.name}` : ''}
            </div>
            <div className="text-sm">{weekLabel(weekStart, weekEnd)}</div>
          </>
        ) : (
          <>
            <div className="text-base font-semibold">
              Attendance concerns — {activeLocationName ?? 'this location'}
            </div>
            <div className="text-sm">
              Last {windowDays} days
              {concernsData
                ? ` · ${weekLabel(concernsData.window.from, concernsData.window.to)}`
                : ''}
            </div>
          </>
        )}
      </div>

      <div className="attendance-print-hide border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Attendance">
          <button
            type="button"
            data-testid="attendance-tab-register"
            onClick={() => setTab('register')}
            aria-current={tab === 'register' ? 'page' : undefined}
            className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
              tab === 'register'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Register
          </button>
          <button
            type="button"
            data-testid="attendance-tab-concerns"
            onClick={() => setTab('concerns')}
            aria-current={tab === 'concerns' ? 'page' : undefined}
            className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
              tab === 'concerns'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Concerns
          </button>
        </nav>
      </div>

      {tab === 'register' ? (
        <div data-testid="attendance-tab-panel-register" className="space-y-4">
          <div className="attendance-print-hide flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="attendance-week-prev"
              onClick={() => setWeekStart((w) => addDays(w, -DAYS_IN_WEEK))}
              aria-label="Previous week"
              className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
            >
              ‹
            </button>
            <span data-testid="attendance-week-label" className="text-sm font-medium text-gray-900">
              {weekLabel(weekStart, weekEnd)}
            </span>
            <button
              type="button"
              data-testid="attendance-week-next"
              onClick={() => setWeekStart((w) => addDays(w, DAYS_IN_WEEK))}
              aria-label="Next week"
              className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
            >
              ›
            </button>
            <select
              data-testid="attendance-class-filter"
              aria-label="Class"
              value={classId ?? ''}
              onChange={(e) => setClassId(e.target.value)}
              className="max-w-xs truncate rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 sm:ml-2"
            >
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <input
              type="search"
              data-testid="attendance-child-search"
              aria-label="Find a child"
              placeholder="Find a child"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-44 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-400"
            />
            {data && (
              <span
                data-testid="attendance-register-count"
                className="text-sm text-gray-500 sm:ml-auto"
              >
                <span className="font-medium text-gray-900">
                  {students.length} {students.length === 1 ? 'child' : 'children'}
                </span>
                {/* With a search on, say how many rows the search kept before
                    how many are drawn — otherwise "34 children · 1 shown"
                    cannot be told apart from a batch cap. */}
                {needle
                  ? ` · ${matching.length} ${matching.length === 1 ? 'match' : 'matches'} · ${
                      shownIds.size
                    } shown`
                  : ` · ${shownIds.size} shown`}
              </span>
            )}
          </div>
          {registerPanel}
        </div>
      ) : (
        <div data-testid="attendance-tab-panel-concerns" className="space-y-4">
          <div className="attendance-print-hide inline-flex rounded-md border border-gray-300 bg-white p-0.5">
            {CONCERN_WINDOWS.map((days) => (
              <button
                key={days}
                type="button"
                data-testid={`attendance-window-${days}`}
                onClick={() => setWindowDays(days)}
                aria-pressed={windowDays === days}
                className={`rounded px-3 py-1 text-sm font-medium ${
                  windowDays === days
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {days} days
              </button>
            ))}
          </div>

          {concernsPanel}

          {/* Guidance, not enforcement: the 4-week ECCE rule is administered by
              the scheme, so this page points at the official source rather
              than encoding a threshold that would go stale. */}
          <div
            data-testid="attendance-concerns-help"
            className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600"
          >
            <p>
              Under the ECCE/NCS funding rules, a child absent for 4 consecutive weeks may lose
              their ECCE place and the absence must be reported. This list is an early warning — it
              shows children who attended no sessions in the selected window.
            </p>
            <a
              href={ECCE_GUIDANCE_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="attendance-concerns-help-link"
              className="mt-2 inline-block text-primary-600 hover:text-primary-500 underline"
            >
              ECCE scheme guidance on citizensinformation.ie
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendancePage;
